import { reconciliationMessages } from "@/services/messages";
import { useTabActions } from "@/stores/tab-store";
import { GitCompare } from "lucide-react";

interface ListEmptyStateProps {
	onCreate: () => void;
}

/**
 * Empty state для страницы списка наборов сверки (FR-019a).
 * Показывается когда в системе ноль наборов.
 */
export function ListEmptyState({ onCreate }: ListEmptyStateProps) {
	return (
		<div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white py-16 px-6 text-center">
			<GitCompare className="h-16 w-16 text-slate-300" aria-hidden="true" />
			<h2 className="mt-4 text-lg font-semibold text-slate-900">
				{reconciliationMessages.list_empty_title}
			</h2>
			<p className="mt-2 max-w-md text-sm text-slate-500">
				{reconciliationMessages.list_empty_subtitle}
			</p>
			<button
				type="button"
				onClick={onCreate}
				className="mt-6 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
			>
				{reconciliationMessages.list_empty_cta}
			</button>
		</div>
	);
}

interface EligibleFilesEmptyStateProps {
	fileRole: "work_order" | "act";
}

/**
 * Inline-empty state для таблицы eligible-файлов (FR-019b).
 * Для work_order — кнопка перехода к загрузке.
 * Для act — две кнопки (загрузка файлов и журнал активности).
 */
export function EligibleFilesEmptyState({ fileRole }: EligibleFilesEmptyStateProps) {
	const { openUploadTab, openActivityLogTab } = useTabActions();

	if (fileRole === "work_order") {
		return (
			<div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
				<p>{reconciliationMessages.eligible_empty_workorders}</p>
				<div className="mt-3">
					<button
						type="button"
						onClick={openUploadTab}
						className="inline-flex items-center rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100"
					>
						{reconciliationMessages.eligible_empty_action_files}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
			<p>{reconciliationMessages.eligible_empty_acts}</p>
			<div className="mt-3 flex flex-wrap gap-2">
				<button
					type="button"
					onClick={openUploadTab}
					className="inline-flex items-center rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100"
				>
					{reconciliationMessages.eligible_empty_action_files_short}
				</button>
				<button
					type="button"
					onClick={openActivityLogTab}
					className="inline-flex items-center rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100"
				>
					{reconciliationMessages.eligible_empty_action_logs}
				</button>
			</div>
		</div>
	);
}
